import React, { useState, useRef } from 'react';
import { useRecoilState } from 'recoil';
import {
  Box, Heading, Button, Tabs, TabList, TabPanels, Tab, TabPanel,
  FormControl, FormLabel, Input, SimpleGrid, Text, VStack, HStack,
  Table, Thead, Tbody, Tr, Th, Td, useToast, Divider, Card, CardBody
} from '@chakra-ui/react';
import { settingsState, defaultSettings } from '../state/settingsState';

const FINAL_HOLE_LABELS = [
  '3-3/4"', '4"', '4-3/4"', '5-7/8"', '6"', '6-1/2"', '7-7/8"', '8-1/2"',
  '8-3/4"', '9-1/2"', '9-7/8"', '10-5/8"', '12-1/4"', '14-3/4"', '16"', '17-1/2"'
];

const SettingInput = ({ label, value, onChange, step = 0.01, min, max }) => (
  <FormControl size="sm">
    <FormLabel fontSize="xs" color="gray.600" mb={1}>{label}</FormLabel>
    <Input
      size="sm"
      type="number"
      step={step}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    />
  </FormControl>
);

const SectionHeader = ({ children }) => (
  <Text fontWeight="600" color="red.600" fontSize="xs" textTransform="uppercase" mt={3} mb={1}>
    {children}
  </Text>
);

export const AdminSettingsPage = () => {
  const [settings, setSettings] = useRecoilState(settingsState);
  const [localSettings, setLocalSettings] = useState(JSON.parse(JSON.stringify(settings)));
  const [hasChanges, setHasChanges] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const updateLocalSettings = (path, value) => {
    setLocalSettings(prev => {
      const newSettings = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
    setHasChanges(true);
  };

  const handleSave = () => {
    setSettings(localSettings);
    setHasChanges(false);
    toast({ title: 'Settings saved', status: 'success', duration: 2000 });
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(localSettings, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'complexity-index-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Settings exported', status: 'success', duration: 2000 });
  };

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        setLocalSettings(imported);
        setHasChanges(true);
        toast({ title: 'Settings imported', status: 'success', duration: 2000 });
      } catch (err) {
        toast({ title: 'Failed to parse file', status: 'error', duration: 3000 });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleReset = () => {
    if (window.confirm('Reset all settings to defaults? This cannot be undone.')) {
      setLocalSettings(JSON.parse(JSON.stringify(defaultSettings)));
      setHasChanges(true);
      toast({ title: 'Settings reset to defaults', status: 'info', duration: 2000 });
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mt="15px" mb={6}>
        <Heading size="lg" color="gray.700">Calculation Settings</Heading>
        <Button colorScheme="green" onClick={handleSave} isDisabled={!hasChanges}>
          Save Changes
        </Button>
      </HStack>

      <Card>
        <CardBody p={0}>
          <Tabs colorScheme="red" size="sm">
            <TabList px={4} pt={2} flexWrap="wrap">
              <Tab>Category Weights</Tab>
              <Tab>Parameter Weights</Tab>
              <Tab>Value Mappings</Tab>
              <Tab>Thresholds</Tab>
              <Tab>Wellbore Geometry</Tab>
              <Tab>Import/Export</Tab>
            </TabList>

            <TabPanels>
              {/* Category Weights */}
              <TabPanel>
                <SectionHeader>Site Level Category Weights</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <SettingInput
                    label="Location"
                    value={localSettings.categoryWeights.location}
                    onChange={(v) => updateLocalSettings('categoryWeights.location', v)}
                  />
                  <SettingInput
                    label="Rig Capacity"
                    value={localSettings.categoryWeights.rigCapacity}
                    onChange={(v) => updateLocalSettings('categoryWeights.rigCapacity', v)}
                  />
                </SimpleGrid>

                <SectionHeader>Well Level Category Weights</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <SettingInput
                    label="Design"
                    value={localSettings.categoryWeights.design}
                    onChange={(v) => updateLocalSettings('categoryWeights.design', v)}
                  />
                  <SettingInput
                    label="Construction"
                    value={localSettings.categoryWeights.construction}
                    onChange={(v) => updateLocalSettings('categoryWeights.construction', v)}
                  />
                  <SettingInput
                    label="Drilling Complexity"
                    value={localSettings.categoryWeights.drillingComplexity}
                    onChange={(v) => updateLocalSettings('categoryWeights.drillingComplexity', v)}
                  />
                  <SettingInput
                    label="Well Control"
                    value={localSettings.categoryWeights.wellControl}
                    onChange={(v) => updateLocalSettings('categoryWeights.wellControl', v)}
                  />
                </SimpleGrid>
              </TabPanel>

              {/* Parameter Weights */}
              <TabPanel>
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                  <Box>
                    <SectionHeader>Location Sensitivity</SectionHeader>
                    <SimpleGrid columns={2} spacing={2}>
                      <SettingInput label="Location" value={localSettings.weights.locationWeight} onChange={(v) => updateLocalSettings('weights.locationWeight', v)} />
                      <SettingInput label="Residential" value={localSettings.weights.residentialWeight} onChange={(v) => updateLocalSettings('weights.residentialWeight', v)} />
                      <SettingInput label="Sensitive" value={localSettings.weights.sensitiveWeight} onChange={(v) => updateLocalSettings('weights.sensitiveWeight', v)} />
                      <SettingInput label="Rig Capacity" value={localSettings.weights.rigCapacityWeight} onChange={(v) => updateLocalSettings('weights.rigCapacityWeight', v)} />
                    </SimpleGrid>

                    <SectionHeader>Construction</SectionHeader>
                    <SimpleGrid columns={3} spacing={2}>
                      <SettingInput label="Circulating Temp" value={localSettings.weights.circulatingTempWeight} onChange={(v) => updateLocalSettings('weights.circulatingTempWeight', v)} />
                      <SettingInput label="Pressure Regime" value={localSettings.weights.pressureRegimeWeight} onChange={(v) => updateLocalSettings('weights.pressureRegimeWeight', v)} />
                      <SettingInput label="UBO/MPD" value={localSettings.weights.ubompdWeight} onChange={(v) => updateLocalSettings('weights.ubompdWeight', v)} />
                    </SimpleGrid>

                    <SectionHeader>Drilling Complexity</SectionHeader>
                    <SimpleGrid columns={3} spacing={2}>
                      <SettingInput label="UCS" value={localSettings.weights.ucsWeight} onChange={(v) => updateLocalSettings('weights.ucsWeight', v)} />
                      <SettingInput label="Interbedded" value={localSettings.weights.interbeddedWeight} onChange={(v) => updateLocalSettings('weights.interbeddedWeight', v)} />
                      <SettingInput label="DDI" value={localSettings.weights.ddiWeight} onChange={(v) => updateLocalSettings('weights.ddiWeight', v)} />
                      <SettingInput label="Multilateral" value={localSettings.weights.multiLateralWeight} onChange={(v) => updateLocalSettings('weights.multiLateralWeight', v)} />
                      <SettingInput label="Interception" value={localSettings.weights.interceptionWeight} onChange={(v) => updateLocalSettings('weights.interceptionWeight', v)} />
                    </SimpleGrid>

                    <SectionHeader>Well Control</SectionHeader>
                    <SimpleGrid columns={3} spacing={2}>
                      <SettingInput label="Phase" value={localSettings.weights.phaseWeight} onChange={(v) => updateLocalSettings('weights.phaseWeight', v)} />
                      <SettingInput label="Hydrocarbons" value={localSettings.weights.hydrocarbonWeight} onChange={(v) => updateLocalSettings('weights.hydrocarbonWeight', v)} />
                      <SettingInput label="Toxic Gas" value={localSettings.weights.toxicGasWeight} onChange={(v) => updateLocalSettings('weights.toxicGasWeight', v)} />
                    </SimpleGrid>
                  </Box>

                  <Box>
                    <SectionHeader>Design</SectionHeader>
                    <SimpleGrid columns={3} spacing={2}>
                      <SettingInput label="First Hole Dia." value={localSettings.weights.firstHoleDiameterWeight} onChange={(v) => updateLocalSettings('weights.firstHoleDiameterWeight', v)} />
                      <SettingInput label="First BOP Dia." value={localSettings.weights.firstBOPDiameterWeight} onChange={(v) => updateLocalSettings('weights.firstBOPDiameterWeight', v)} />
                      <SettingInput label="Sections" value={localSettings.weights.numberOfSectionsWeight} onChange={(v) => updateLocalSettings('weights.numberOfSectionsWeight', v)} />
                      <SettingInput label="Vertical Depth" value={localSettings.weights.verticalDepthWeight} onChange={(v) => updateLocalSettings('weights.verticalDepthWeight', v)} />
                      <SettingInput label="Max Temp" value={localSettings.weights.maxTemperatureWeight} onChange={(v) => updateLocalSettings('weights.maxTemperatureWeight', v)} />
                      <SettingInput label="Max Pressure" value={localSettings.weights.maxPressureWeight} onChange={(v) => updateLocalSettings('weights.maxPressureWeight', v)} />
                      <SettingInput label="Stimulation" value={localSettings.weights.stimulationWeight} onChange={(v) => updateLocalSettings('weights.stimulationWeight', v)} />
                      <SettingInput label="Scaling" value={localSettings.weights.scalingWeight} onChange={(v) => updateLocalSettings('weights.scalingWeight', v)} />
                      <SettingInput label="Corrosion" value={localSettings.weights.corrosionWeight} onChange={(v) => updateLocalSettings('weights.corrosionWeight', v)} />
                    </SimpleGrid>

                    <SectionHeader>Section Scores (by count)</SectionHeader>
                    <SimpleGrid columns={7} spacing={1}>
                      {localSettings.weights.numberOfSectionsScore.map((val, idx) => (
                        <SettingInput
                          key={idx}
                          label={`${idx + 1}`}
                          value={val}
                          onChange={(v) => {
                            const newArr = [...localSettings.weights.numberOfSectionsScore];
                            newArr[idx] = v;
                            updateLocalSettings('weights.numberOfSectionsScore', newArr);
                          }}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                </SimpleGrid>
              </TabPanel>

              {/* Value Mappings */}
              <TabPanel>
                <SectionHeader>Location Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {Object.entries(localSettings.locationValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.1} onChange={(v) => updateLocalSettings(`locationValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>Rig Capacity Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
                  {Object.entries(localSettings.rigCapacityValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.5} onChange={(v) => updateLocalSettings(`rigCapacityValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>Circulation Temperature Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  {Object.entries(localSettings.circulationValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.1} onChange={(v) => updateLocalSettings(`circulationValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>Pressure Regime Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  {Object.entries(localSettings.pressRegimeValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.1} onChange={(v) => updateLocalSettings(`pressRegimeValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>UCS Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {Object.entries(localSettings.ucsValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.01} onChange={(v) => updateLocalSettings(`ucsValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>DDI Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {Object.entries(localSettings.ddiValues).filter(([key]) => key !== 'calculated').map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.01} onChange={(v) => updateLocalSettings(`ddiValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>

                <SectionHeader>Phase Values</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  {Object.entries(localSettings.phaseValues).map(([key, obj]) => (
                    <SettingInput key={key} label={obj.txt} value={obj.val} step={0.1} onChange={(v) => updateLocalSettings(`phaseValues.${key}.val`, v)} />
                  ))}
                </SimpleGrid>
              </TabPanel>

              {/* Thresholds */}
              <TabPanel>
                <SectionHeader>Location Multipliers</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <SettingInput label="Rural" value={localSettings.thresholds.locationMultipliers.verylow} step={1} onChange={(v) => updateLocalSettings('thresholds.locationMultipliers.verylow', v)} />
                  <SettingInput label="Industrial" value={localSettings.thresholds.locationMultipliers.low} step={1} onChange={(v) => updateLocalSettings('thresholds.locationMultipliers.low', v)} />
                  <SettingInput label="Urban" value={localSettings.thresholds.locationMultipliers.medium} step={1} onChange={(v) => updateLocalSettings('thresholds.locationMultipliers.medium', v)} />
                  <SettingInput label="Offshore" value={localSettings.thresholds.locationMultipliers.high} step={1} onChange={(v) => updateLocalSettings('thresholds.locationMultipliers.high', v)} />
                </SimpleGrid>

                <SectionHeader>First Hole Diameter Thresholds (inches)</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4}>
                  <SettingInput label="Low Threshold" value={localSettings.thresholds.firstHoleDiameter.low} step={0.5} onChange={(v) => updateLocalSettings('thresholds.firstHoleDiameter.low', v)} />
                  <SettingInput label="High Threshold" value={localSettings.thresholds.firstHoleDiameter.high} step={0.5} onChange={(v) => updateLocalSettings('thresholds.firstHoleDiameter.high', v)} />
                </SimpleGrid>

                <SectionHeader>First BOP Thresholds (inches)</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                  <SettingInput label="Threshold 1" value={localSettings.thresholds.firstBOP.t1} step={0.125} onChange={(v) => updateLocalSettings('thresholds.firstBOP.t1', v)} />
                  <SettingInput label="Threshold 2" value={localSettings.thresholds.firstBOP.t2} step={0.125} onChange={(v) => updateLocalSettings('thresholds.firstBOP.t2', v)} />
                  <SettingInput label="Threshold 3" value={localSettings.thresholds.firstBOP.t3} step={0.125} onChange={(v) => updateLocalSettings('thresholds.firstBOP.t3', v)} />
                </SimpleGrid>

                <SectionHeader>Depth</SectionHeader>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <SettingInput label="Max Reference Depth (m)" value={localSettings.thresholds.maxDepth} step={100} onChange={(v) => updateLocalSettings('thresholds.maxDepth', v)} />
                </SimpleGrid>

                <SectionHeader>Temperature Thresholds (C)</SectionHeader>
                <SimpleGrid columns={{ base: 2, md: 2 }} spacing={4}>
                  <SettingInput label="Min Temperature" value={localSettings.thresholds.temperature.min} step={1} onChange={(v) => updateLocalSettings('thresholds.temperature.min', v)} />
                  <SettingInput label="Max Temperature" value={localSettings.thresholds.temperature.max} step={1} onChange={(v) => updateLocalSettings('thresholds.temperature.max', v)} />
                </SimpleGrid>

                <SectionHeader>Pressure Thresholds (PSI)</SectionHeader>
                <SimpleGrid columns={{ base: 3, md: 5 }} spacing={4}>
                  {localSettings.thresholds.pressure.map((val, idx) => (
                    <SettingInput
                      key={idx}
                      label={`Level ${idx + 1}`}
                      value={val}
                      step={100}
                      onChange={(v) => {
                        const newArr = [...localSettings.thresholds.pressure];
                        newArr[idx] = v;
                        updateLocalSettings('thresholds.pressure', newArr);
                      }}
                    />
                  ))}
                </SimpleGrid>

                <SectionHeader>Conversion</SectionHeader>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <SettingInput label="Bar to PSI Factor" value={localSettings.thresholds.barToPsi} step={0.0001} onChange={(v) => updateLocalSettings('thresholds.barToPsi', v)} />
                  <SettingInput label="Rounding Decimals" value={localSettings.roundingDecimals} step={1} onChange={(v) => updateLocalSettings('roundingDecimals', v)} />
                </SimpleGrid>
              </TabPanel>

              {/* Wellbore Geometry */}
              <TabPanel>
                <SectionHeader>Wellbore Geometry Table</SectionHeader>
                <Text fontSize="xs" color="gray.500" mb={3}>Final hole diameter rows x Section columns</Text>
                <Box overflowX="auto">
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Final Hole</Th>
                        {[1, 2, 3, 4, 5, 6, 7].map(n => <Th key={n} textAlign="center" px={1}>{n} Sec</Th>)}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {Object.entries(localSettings.wellboreGeometry).map(([rowKey, rowVals]) => (
                        <Tr key={rowKey}>
                          <Td fontWeight="500" fontSize="xs">{FINAL_HOLE_LABELS[parseInt(rowKey) - 1]}</Td>
                          {rowVals.map((val, colIdx) => (
                            <Td key={colIdx} p={1} textAlign="center">
                              <Input
                                size="xs"
                                type="number"
                                step="0.001"
                                value={val}
                                w="60px"
                                textAlign="center"
                                onChange={(e) => {
                                  const newArr = [...rowVals];
                                  newArr[colIdx] = parseFloat(e.target.value) || 0;
                                  updateLocalSettings(`wellboreGeometry.${rowKey}`, newArr);
                                }}
                              />
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </TabPanel>

              {/* Import/Export */}
              <TabPanel>
                <VStack spacing={6} align="stretch">
                  <Box>
                    <SectionHeader>Export Settings</SectionHeader>
                    <Text fontSize="sm" color="gray.600" mb={3}>Download current settings as a JSON file</Text>
                    <Button colorScheme="blue" onClick={handleExport}>Export Settings</Button>
                  </Box>

                  <Divider />

                  <Box>
                    <SectionHeader>Import Settings</SectionHeader>
                    <Text fontSize="sm" color="gray.600" mb={3}>Upload a previously exported settings file</Text>
                    <input type="file" accept=".json" ref={fileInputRef} onChange={handleImport} style={{ display: 'none' }} />
                    <Button colorScheme="blue" variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Choose File & Import
                    </Button>
                  </Box>

                  <Divider />

                  <Box>
                    <SectionHeader>Reset to Defaults</SectionHeader>
                    <Text fontSize="sm" color="gray.600" mb={3}>Restore all settings to their original values</Text>
                    <Button colorScheme="red" variant="outline" onClick={handleReset}>Reset to Defaults</Button>
                  </Box>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AdminSettingsPage;
